// How big are they? Generated by scripts/build-sizes.py: do not edit by hand.
// Each body's mean diameter with the table or paper it comes from, and its picture: a real photograph cut out on a
// transparent background (public/sizes/<id>.webp), with its credit, licence and its width and height in kilometres,
// so the boards can draw every picture to one scale. Bodies with no close-up photograph have none.

export type SizeLicence = 'Public domain (NASA)' | 'CC BY 4.0' | 'ESA (with permission)' | 'Used with permission (granted 6 Oct 2026)';
export type SizeKind = 'star' | 'planet' | 'moon' | 'dwarf' | 'candidate' | 'asteroid' | 'comet' | 'kbo' | 'storm';
/** natural: as the eye would see it; approx: close to it; enhanced: colours strengthened; false: colours from light
 * the eye cannot see, or not as it sees them; bw: black and white; added: colour added to a single-colour picture. */
export type SizeColour = 'natural' | 'approx' | 'enhanced' | 'false' | 'bw' | 'added';

export interface SizePhoto {
  /** Under public/, e.g. 'sizes/earth.webp'. */
  file: string;
  /** A short hash of the file, added to its address so a re-cut picture is never served from an old cache. */
  v: string;
  /** The cut-out's size in pixels. */
  w: number;
  h: number;
  /** The cut-out's width and height in kilometres: drawn at one scale, every body comes out at its true size. */
  wKm: number;
  hKm: number;
  /** Exactly as the source gives it. */
  credit: string;
  licence: SizeLicence;
  /** The page the picture comes from. */
  page: string;
  /** The spacecraft or telescope. */
  by: string;
  /** When it was taken (YYYY, YYYY-MM or YYYY-MM-DD). */
  date: string;
  colour: SizeColour;
  /** The picture fills its box as a disc (or Jupiter's ellipse), night side and all: the app can trace its edge. */
  round?: boolean;
}

export interface SizeBody {
  id: string;
  name: string;
  kind: SizeKind;
  /** A moon's planet (or dwarf planet), by id. */
  parent?: string;
  /** Mean diameter in km, as `basis` describes it: twice the mean (or average) radius, the diameter of a ball of the
   * same volume, or a width measured as a star passed behind it. Sets "× Earth". */
  km: number;
  /** Stated dimensions in km for flattened, elongated or odd-shaped worlds, longest first. */
  dims?: number[];
  /** How the mean diameter is defined, in plain words. */
  basis: string;
  source: { label: string; url: string };
  photo?: SizePhoto;
  /** No picture: what the plain shape says instead ("No close-up photograph"). */
  blank?: string;
  /** Drawn as a plain ellipse of its longest and shortest axes (Haumea). */
  shape?: 'ellipse';
  note?: string;
}

/** Earth's mean diameter (km), twice its mean radius of 6,371 km: the yardstick for "× Earth". */
export const EARTH_KM = 12742;

export const SIZE_BODIES: SizeBody[] = [
  {
    "id": "sun",
    "name": "The Sun",
    "kind": "star",
    "km": 1391400.0,
    "basis": "twice the mean radius, 695,700 km",
    "source": {
      "label": "NASA Sun fact sheet",
      "url": "https://nssdc.gsfc.nasa.gov/planetary/factsheet/sunfact.html"
    },
    "photo": {
      "file": "sizes/sun.webp",
      "w": 2048,
      "h": 2048,
      "wKm": 1391400.0,
      "hKm": 1391400.0,
      "credit": "NASA's Scientific Visualization Studio",
      "licence": "Public domain (NASA)",
      "page": "https://svs.gsfc.nasa.gov/4907/",
      "by": "Solar Dynamics Observatory",
      "date": "2014-10-24",
      "colour": "added",
      "round": true,
      "v": "f4d9b015"
    },
    "note": "Visible light from the Solar Dynamics Observatory, with the large sunspot group of October 2014. The Sun gives white light; the orange is added."
  },
  {
    "id": "mercury",
    "name": "Mercury",
    "kind": "planet",
    "km": 4879.4,
    "basis": "twice the mean radius, 2,439.7 km",
    "source": {
      "label": "NASA Mercury fact sheet",
      "url": "https://nssdc.gsfc.nasa.gov/planetary/factsheet/mercuryfact.html"
    },
    "photo": {
      "file": "sizes/mercury.webp",
      "w": 512,
      "h": 512,
      "wKm": 4879.4,
      "hKm": 4879.4,
      "credit": "NASA/Johns Hopkins University Applied Physics Laboratory/Arizona State University/Carnegie Institution of Washington",
      "licence": "Used with permission (granted 6 Oct 2026)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA11364",
      "by": "MESSENGER",
      "date": "2008-10-06",
      "colour": "approx",
      "round": true,
      "v": "6ec7cbe8"
    },
    "note": "MESSENGER, October 2008. Mercury’s true colours are very subtle; this version gives three colour filters equal weight."
  },
  {
    "id": "venus",
    "name": "Venus",
    "kind": "planet",
    "km": 12103.6,
    "basis": "twice the mean radius, 6,051.8 km",
    "source": {
      "label": "NASA Venus fact sheet",
      "url": "https://nssdc.gsfc.nasa.gov/planetary/factsheet/venusfact.html"
    },
    "photo": {
      "file": "sizes/venus.webp",
      "w": 512,
      "h": 512,
      "wKm": 12103.6,
      "hKm": 12103.6,
      "credit": "NASA/JPL-Caltech",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA23791",
      "by": "Mariner 10",
      "date": "1974-02",
      "colour": "false",
      "round": true,
      "v": "3a142eae"
    },
    "note": "Mariner 10, February 1974, reprocessed in 2020: false colour made from orange and ultraviolet pictures. Only the clouds show."
  },
  {
    "id": "earth",
    "name": "Earth",
    "kind": "planet",
    "km": 12742.0,
    "basis": "twice the mean radius, 6,371 km",
    "source": {
      "label": "NASA Earth fact sheet",
      "url": "https://nssdc.gsfc.nasa.gov/planetary/factsheet/earthfact.html"
    },
    "photo": {
      "file": "sizes/earth.webp",
      "w": 1024,
      "h": 1024,
      "wKm": 12742.0,
      "hKm": 12742.0,
      "credit": "NASA",
      "licence": "Public domain (NASA)",
      "page": "https://images.nasa.gov/details/as17-148-22727",
      "by": "Apollo 17",
      "date": "1972-12-07",
      "colour": "natural",
      "round": true,
      "v": "d37df1e6"
    },
    "note": "Taken by the Apollo 17 crew on 7 December 1972, on their way to the Moon."
  },
  {
    "id": "moon",
    "name": "The Moon",
    "kind": "moon",
    "parent": "earth",
    "km": 3474.8,
    "basis": "twice the mean radius, 1,737.4 km",
    "source": {
      "label": "NASA Moon fact sheet",
      "url": "https://nssdc.gsfc.nasa.gov/planetary/factsheet/moonfact.html"
    },
    "photo": {
      "file": "sizes/moon.webp",
      "w": 512,
      "h": 512,
      "wKm": 3474.8,
      "hKm": 3474.8,
      "credit": "NASA",
      "licence": "Public domain (NASA)",
      "page": "https://images.nasa.gov/details/as11-44-6667",
      "by": "Apollo 11",
      "date": "1969-07-21",
      "colour": "natural",
      "round": true,
      "v": "89619e6f"
    },
    "note": "Taken by the Apollo 11 crew on their way home in July 1969."
  },
  {
    "id": "mars",
    "name": "Mars",
    "kind": "planet",
    "km": 6779.0,
    "basis": "twice the mean radius, 3,389.5 km",
    "source": {
      "label": "NASA Mars fact sheet",
      "url": "https://nssdc.gsfc.nasa.gov/planetary/factsheet/marsfact.html"
    },
    "photo": {
      "file": "sizes/mars.webp",
      "w": 512,
      "h": 512,
      "wKm": 6779.0,
      "hKm": 6779.0,
      "credit": "NASA/ESA, J. Bell (Cornell U.) and M. Wolff (SSI)",
      "licence": "CC BY 4.0",
      "page": "https://esahubble.org/images/opo0322a/",
      "by": "Hubble Space Telescope",
      "date": "2003-08-27",
      "colour": "natural",
      "round": true,
      "v": "e1dee684"
    },
    "note": "Hubble, 27 August 2003, when Mars came within about 56 million kilometres of Earth."
  },
  {
    "id": "jupiter",
    "name": "Jupiter",
    "kind": "planet",
    "km": 139822.0,
    "dims": [
      142984.0,
      133708.0
    ],
    "basis": "twice the mean radius, 69,911 km",
    "source": {
      "label": "NASA Jupiter fact sheet",
      "url": "https://nssdc.gsfc.nasa.gov/planetary/factsheet/jupiterfact.html"
    },
    "photo": {
      "file": "sizes/jupiter.webp",
      "w": 1024,
      "h": 958,
      "wKm": 142984.0,
      "hKm": 133725.04,
      "credit": "NASA, ESA, A. Simon (Goddard Space Flight Center), and M.H. Wong (University of California, Berkeley)",
      "licence": "CC BY 4.0",
      "page": "https://esahubble.org/images/heic1914a/",
      "by": "Hubble Space Telescope",
      "date": "2019-06-27",
      "colour": "approx",
      "round": true,
      "v": "460e0706"
    },
    "note": "Jupiter spins so fast that it bulges: 142,984 km across the equator and 133,708 km from pole to pole. It is drawn in that shape; 139,822 km is its mean diameter."
  },
  {
    "id": "redspot",
    "name": "Jupiter’s Great Red Spot",
    "kind": "storm",
    "km": 16000.0,
    "basis": "its width from west to east, about 16,000 km, as NASA gave it in 2019",
    "source": {
      "label": "NASA, A Storm of Change, 2019",
      "url": "https://science.nasa.gov/photojournal/a-storm-of-change/"
    },
    "photo": {
      "file": "sizes/redspot.webp",
      "w": 658,
      "h": 376,
      "wKm": 42000.0,
      "hKm": 24000.0,
      "credit": "NASA, ESA, A. Simon (Goddard Space Flight Center), and M.H. Wong (University of California, Berkeley)",
      "licence": "CC BY 4.0",
      "page": "https://esahubble.org/images/heic1914a/",
      "by": "Hubble Space Telescope",
      "date": "2019-06-27",
      "colour": "approx",
      "v": "2de2ed37"
    },
    "note": "Part of Hubble’s picture of Jupiter from June 2019, at the same scale. Since Voyager passed in 1979 the spot has shrunk from about 1.8 times Earth’s width to about 1.3 times (NASA)."
  },
  {
    "id": "saturn",
    "name": "Saturn",
    "kind": "planet",
    "km": 116464.0,
    "dims": [
      120536.0,
      108728.0
    ],
    "basis": "twice the mean radius, 58,232 km",
    "source": {
      "label": "NASA Saturn fact sheet",
      "url": "https://nssdc.gsfc.nasa.gov/planetary/factsheet/saturnfact.html"
    },
    "photo": {
      "file": "sizes/saturn.webp",
      "w": 1577,
      "h": 646,
      "wKm": 274604.86,
      "hKm": 112547.22,
      "credit": "NASA, ESA, A. Simon (Goddard Space Flight Center), and M.H. Wong (University of California, Berkeley)",
      "licence": "CC BY 4.0",
      "page": "https://esahubble.org/images/heic1917a/",
      "by": "Hubble Space Telescope",
      "date": "2019-06-20",
      "colour": "approx",
      "v": "7482328a"
    },
    "note": "Saturn is 120,536 km across the equator and 108,728 km from pole to pole. Its main rings, drawn to the same scale, reach 273,560 km from edge to edge."
  },
  {
    "id": "uranus",
    "name": "Uranus",
    "kind": "planet",
    "km": 50724.0,
    "basis": "twice the mean radius, 25,362 km",
    "source": {
      "label": "NASA Uranus fact sheet",
      "url": "https://nssdc.gsfc.nasa.gov/planetary/factsheet/uranusfact.html"
    },
    "photo": {
      "file": "sizes/uranus.webp",
      "w": 512,
      "h": 512,
      "wKm": 50724.0,
      "hKm": 50724.0,
      "credit": "NASA/JPL-Caltech",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA18182",
      "by": "Voyager 2",
      "date": "1986-01",
      "colour": "natural",
      "round": true,
      "v": "2ece26ac"
    },
    "note": "Voyager 2, January 1986, the only spacecraft to have visited Uranus."
  },
  {
    "id": "neptune",
    "name": "Neptune",
    "kind": "planet",
    "km": 49244.0,
    "basis": "twice the mean radius, 24,622 km",
    "source": {
      "label": "NASA Neptune fact sheet",
      "url": "https://nssdc.gsfc.nasa.gov/planetary/factsheet/neptunefact.html"
    },
    "photo": {
      "file": "sizes/neptune.webp",
      "w": 512,
      "h": 512,
      "wKm": 49244.0,
      "hKm": 49244.0,
      "credit": "NASA/JPL",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA01492",
      "by": "Voyager 2",
      "date": "1989-08",
      "colour": "enhanced",
      "round": true,
      "v": "17ec7d4a"
    },
    "note": "Voyager 2, August 1989. The deep blue is enhanced; Neptune is a paler greenish blue, much like Uranus."
  },
  {
    "id": "pluto",
    "name": "Pluto",
    "kind": "dwarf",
    "km": 2376.0,
    "basis": "twice the mean radius, 1,188 km",
    "source": {
      "label": "NASA Pluto fact sheet",
      "url": "https://nssdc.gsfc.nasa.gov/planetary/factsheet/plutofact.html"
    },
    "photo": {
      "file": "sizes/pluto.webp",
      "w": 512,
      "h": 512,
      "wKm": 2376.0,
      "hKm": 2376.0,
      "credit": "NASA/Johns Hopkins University Applied Physics Laboratory/Southwest Research Institute",
      "licence": "Used with permission (granted 6 Oct 2026)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA19857",
      "by": "New Horizons",
      "date": "2015-07-13",
      "colour": "natural",
      "round": true,
      "v": "dc1a9712"
    },
    "note": "New Horizons, July 2015."
  },
  {
    "id": "charon",
    "name": "Charon",
    "kind": "moon",
    "parent": "pluto",
    "km": 1212.0,
    "basis": "twice the mean radius, 606.0 km",
    "source": {
      "label": "JPL satellite physical parameters",
      "url": "https://ssd.jpl.nasa.gov/sats/phys_par/"
    },
    "photo": {
      "file": "sizes/charon.webp",
      "w": 384,
      "h": 384,
      "wKm": 1212.0,
      "hKm": 1212.0,
      "credit": "NASA/Johns Hopkins University Applied Physics Laboratory/Southwest Research Institute",
      "licence": "Used with permission (granted 6 Oct 2026)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA19709",
      "by": "New Horizons",
      "date": "2015-07-13",
      "colour": "approx",
      "round": true,
      "v": "7a369a4d"
    },
    "note": "New Horizons, July 2015, with colour from its second camera added."
  },
  {
    "id": "ceres",
    "name": "Ceres",
    "kind": "dwarf",
    "km": 939.4,
    "dims": [
      964.4,
      964.2,
      891.8
    ],
    "basis": "the diameter of a ball of the same volume (Park and others, 2016)",
    "source": {
      "label": "JPL Small-Body Database",
      "url": "https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=1"
    },
    "photo": {
      "file": "sizes/ceres.webp",
      "w": 384,
      "h": 384,
      "wKm": 939.4,
      "hKm": 939.4,
      "credit": "NASA/JPL-Caltech/UCLA/MPS/DLR/IDA",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA19562",
      "by": "Dawn",
      "date": "2015-05-05",
      "colour": "bw",
      "round": true,
      "v": "f9b159ef"
    },
    "note": "Dawn, May 2015, from 13,600 km. Ceres is the largest body in the asteroid belt."
  },
  {
    "id": "vesta",
    "name": "Vesta",
    "kind": "asteroid",
    "km": 522.77,
    "dims": [
      569.24,
      554.48,
      452.66
    ],
    "basis": "the diameter of a ball of the same volume (Park and others, 2025)",
    "source": {
      "label": "JPL Small-Body Database",
      "url": "https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=4"
    },
    "photo": {
      "file": "sizes/vesta.webp",
      "w": 384,
      "h": 315,
      "wKm": 571.95,
      "hKm": 469.06,
      "credit": "NASA/JPL-Caltech/UCLA/MPS/DLR/IDA",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA15678",
      "by": "Dawn",
      "date": "2012",
      "colour": "bw",
      "v": "e997e103"
    },
    "note": "Dawn, 2011 to 2012, seen from the side with its south pole at the bottom. Its width across the picture is taken as 562 km, the middle of its two longer axes."
  },
  {
    "id": "eris",
    "name": "Eris",
    "kind": "dwarf",
    "km": 2326.0,
    "basis": "measured as a star passed behind it in 2010 (2,326 km, give or take 12 km)",
    "source": {
      "label": "Sicardy and others, 2011 (ESO)",
      "url": "https://www.eso.org/public/news/eso1142/"
    },
    "blank": "No close-up photograph",
    "note": "No spacecraft has visited Eris, so no picture shows its surface."
  },
  {
    "id": "haumea",
    "name": "Haumea",
    "kind": "dwarf",
    "km": 1595.0,
    "dims": [
      2322.0,
      1704.0,
      1026.0
    ],
    "basis": "a ball of the same volume as its measured shape, 2,322 by 1,704 by 1,026 km",
    "source": {
      "label": "Ortiz and others, 2017, Nature",
      "url": "https://arxiv.org/abs/2006.03113"
    },
    "blank": "No close-up photograph",
    "shape": "ellipse",
    "note": "Haumea spins in under four hours and is stretched into an egg shape. It is drawn as a plain ellipse 2,322 km long and 1,026 km high, its longest and shortest axes."
  },
  {
    "id": "makemake",
    "name": "Makemake",
    "kind": "dwarf",
    "km": 1430.0,
    "basis": "about 1,430 km, as NASA gives it; a star passing behind it in 2011 showed 1,430 by 1,502 km (Ortiz and others, 2012)",
    "source": {
      "label": "NASA Makemake page",
      "url": "https://science.nasa.gov/dwarf-planets/makemake/"
    },
    "blank": "No close-up photograph",
    "note": "No spacecraft has visited Makemake."
  },
  {
    "id": "gonggong",
    "name": "Gonggong",
    "kind": "candidate",
    "km": 1230.0,
    "basis": "about 1,230 km, give or take 50 km",
    "source": {
      "label": "Kiss and others, 2019, Icarus",
      "url": "https://arxiv.org/abs/1903.05439"
    },
    "blank": "No close-up photograph",
    "note": "A possible dwarf planet, not yet confirmed as one."
  },
  {
    "id": "quaoar",
    "name": "Quaoar",
    "kind": "candidate",
    "km": 1086.0,
    "basis": "the width of a disc with the same area as its outline, seen as stars passed behind it (a radius of 543 km)",
    "source": {
      "label": "Pereira and others, 2023, Astronomy & Astrophysics",
      "url": "https://arxiv.org/abs/2304.09237"
    },
    "blank": "No close-up photograph",
    "note": "A possible dwarf planet, not yet confirmed as one."
  },
  {
    "id": "sedna",
    "name": "Sedna",
    "kind": "candidate",
    "km": 995.0,
    "basis": "about 995 km, give or take 80 km",
    "source": {
      "label": "Pál and others, 2012, Astronomy & Astrophysics",
      "url": "https://arxiv.org/abs/1204.0899"
    },
    "blank": "No close-up photograph",
    "note": "A possible dwarf planet, not yet confirmed as one."
  },
  {
    "id": "ganymede",
    "name": "Ganymede",
    "kind": "moon",
    "parent": "jupiter",
    "km": 5262.4,
    "basis": "twice the mean radius, 2,631.2 km",
    "source": {
      "label": "JPL satellite physical parameters",
      "url": "https://ssd.jpl.nasa.gov/sats/phys_par/"
    },
    "photo": {
      "file": "sizes/ganymede.webp",
      "w": 384,
      "h": 384,
      "wKm": 5262.4,
      "hKm": 5262.4,
      "credit": "NASA/JPL",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA00716",
      "by": "Galileo",
      "date": "1996-06-26",
      "colour": "natural",
      "round": true,
      "v": "0ca205c0"
    },
    "note": "Galileo, June 1996. The largest moon in the Solar System, wider than Mercury."
  },
  {
    "id": "titan",
    "name": "Titan",
    "kind": "moon",
    "parent": "saturn",
    "km": 5149.52,
    "basis": "twice the mean radius, 2,574.76 km",
    "source": {
      "label": "JPL satellite physical parameters",
      "url": "https://ssd.jpl.nasa.gov/sats/phys_par/"
    },
    "photo": {
      "file": "sizes/titan.webp",
      "w": 384,
      "h": 384,
      "wKm": 5149.52,
      "hKm": 5149.52,
      "credit": "NASA/JPL-Caltech/Space Science Institute",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA14602",
      "by": "Cassini",
      "date": "2012-01-30",
      "colour": "natural",
      "round": true,
      "v": "d83f27c9"
    },
    "note": "Cassini, January 2012. Drawn at the size of its solid surface; the orange haze shown here rises a few hundred kilometres higher."
  },
  {
    "id": "callisto",
    "name": "Callisto",
    "kind": "moon",
    "parent": "jupiter",
    "km": 4820.6,
    "basis": "twice the mean radius, 2,410.3 km",
    "source": {
      "label": "JPL satellite physical parameters",
      "url": "https://ssd.jpl.nasa.gov/sats/phys_par/"
    },
    "photo": {
      "file": "sizes/callisto.webp",
      "w": 384,
      "h": 384,
      "wKm": 4820.6,
      "hKm": 4820.6,
      "credit": "NASA/JPL/DLR",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA03456",
      "by": "Galileo",
      "date": "2001-05",
      "colour": "approx",
      "round": true,
      "v": "0972f8b6"
    },
    "note": "Galileo, May 2001: its only complete colour picture of Callisto."
  },
  {
    "id": "io",
    "name": "Io",
    "kind": "moon",
    "parent": "jupiter",
    "km": 3642.98,
    "basis": "twice the mean radius, 1,821.49 km",
    "source": {
      "label": "JPL satellite physical parameters",
      "url": "https://ssd.jpl.nasa.gov/sats/phys_par/"
    },
    "photo": {
      "file": "sizes/io.webp",
      "w": 384,
      "h": 384,
      "wKm": 3642.98,
      "hKm": 3642.98,
      "credit": "NASA/JPL/University of Arizona",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA02308",
      "by": "Galileo",
      "date": "1999-07-03",
      "colour": "approx",
      "round": true,
      "v": "80d8e486"
    },
    "note": "Galileo, July 1999, in colours close to what the eye would see."
  },
  {
    "id": "europa",
    "name": "Europa",
    "kind": "moon",
    "parent": "jupiter",
    "km": 3121.6,
    "basis": "twice the mean radius, 1,560.8 km",
    "source": {
      "label": "JPL satellite physical parameters",
      "url": "https://ssd.jpl.nasa.gov/sats/phys_par/"
    },
    "photo": {
      "file": "sizes/europa.webp",
      "w": 242,
      "h": 242,
      "wKm": 3121.6,
      "hKm": 3121.6,
      "credit": "NASA/JPL/University of Arizona",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA01295",
      "by": "Galileo",
      "date": "1997-06",
      "colour": "natural",
      "round": true,
      "v": "15706cd6"
    },
    "note": "Galileo, June 1997, in natural colour."
  },
  {
    "id": "triton",
    "name": "Triton",
    "kind": "moon",
    "parent": "neptune",
    "km": 2705.2,
    "basis": "twice the mean radius, 1,352.6 km",
    "source": {
      "label": "JPL satellite physical parameters",
      "url": "https://ssd.jpl.nasa.gov/sats/phys_par/"
    },
    "photo": {
      "file": "sizes/triton.webp",
      "w": 182,
      "h": 182,
      "wKm": 2705.2,
      "hKm": 2705.2,
      "credit": "NASA/JPL",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA02246",
      "by": "Voyager 2",
      "date": "1989-08-20",
      "colour": "approx",
      "round": true,
      "v": "d4088506"
    },
    "note": "Voyager 2, 20 August 1989, five days before it flew past, while still 5.4 million km from Neptune."
  },
  {
    "id": "titania",
    "name": "Titania",
    "kind": "moon",
    "parent": "uranus",
    "km": 1577.8,
    "basis": "twice the mean radius, 788.9 km",
    "source": {
      "label": "JPL satellite physical parameters",
      "url": "https://ssd.jpl.nasa.gov/sats/phys_par/"
    },
    "photo": {
      "file": "sizes/titania.webp",
      "w": 256,
      "h": 256,
      "wKm": 1577.8,
      "hKm": 1577.8,
      "credit": "NASA/JPL",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA01979",
      "by": "Voyager 2",
      "date": "1986-01-24",
      "colour": "bw",
      "round": true,
      "v": "a4e79e47"
    },
    "note": "Voyager 2, January 1986."
  },
  {
    "id": "rhea",
    "name": "Rhea",
    "kind": "moon",
    "parent": "saturn",
    "km": 1527.0,
    "basis": "twice the mean radius, 763.5 km",
    "source": {
      "label": "JPL satellite physical parameters",
      "url": "https://ssd.jpl.nasa.gov/sats/phys_par/"
    },
    "photo": {
      "file": "sizes/rhea.webp",
      "w": 256,
      "h": 256,
      "wKm": 1527.0,
      "hKm": 1527.0,
      "credit": "NASA/JPL/Space Science Institute",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA06578",
      "by": "Cassini",
      "date": "2005",
      "colour": "natural",
      "round": true,
      "v": "08d8eedf"
    },
    "note": "Cassini, 2005, in natural colour."
  },
  {
    "id": "oberon",
    "name": "Oberon",
    "kind": "moon",
    "parent": "uranus",
    "km": 1522.8,
    "basis": "twice the mean radius, 761.4 km",
    "source": {
      "label": "JPL satellite physical parameters",
      "url": "https://ssd.jpl.nasa.gov/sats/phys_par/"
    },
    "photo": {
      "file": "sizes/oberon.webp",
      "w": 256,
      "h": 256,
      "wKm": 1522.8,
      "hKm": 1522.8,
      "credit": "NASA/JPL",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA00034",
      "by": "Voyager 2",
      "date": "1986-01-24",
      "colour": "approx",
      "round": true,
      "v": "900e0903"
    },
    "note": "Voyager 2, January 1986."
  },
  {
    "id": "iapetus",
    "name": "Iapetus",
    "kind": "moon",
    "parent": "saturn",
    "km": 1468.6,
    "basis": "twice the mean radius, 734.3 km",
    "source": {
      "label": "JPL satellite physical parameters",
      "url": "https://ssd.jpl.nasa.gov/sats/phys_par/"
    },
    "photo": {
      "file": "sizes/iapetus.webp",
      "w": 256,
      "h": 256,
      "wKm": 1468.6,
      "hKm": 1468.6,
      "credit": "NASA/JPL/Space Science Institute",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA11690",
      "by": "Cassini",
      "date": "2004-12-27",
      "colour": "false",
      "round": true,
      "v": "60a3f7a7"
    },
    "note": "Cassini, December 2004, in false colour (infrared, green and ultraviolet): the dark side that leads as Iapetus goes round Saturn."
  },
  {
    "id": "umbriel",
    "name": "Umbriel",
    "kind": "moon",
    "parent": "uranus",
    "km": 1169.4,
    "basis": "twice the mean radius, 584.7 km",
    "source": {
      "label": "JPL satellite physical parameters",
      "url": "https://ssd.jpl.nasa.gov/sats/phys_par/"
    },
    "photo": {
      "file": "sizes/umbriel.webp",
      "w": 230,
      "h": 230,
      "wKm": 1169.4,
      "hKm": 1169.4,
      "credit": "NASA/JPL",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA00040",
      "by": "Voyager 2",
      "date": "1986-01-24",
      "colour": "bw",
      "round": true,
      "v": "8e98d641"
    },
    "note": "Voyager 2, January 1986. Part of it was in darkness."
  },
  {
    "id": "ariel",
    "name": "Ariel",
    "kind": "moon",
    "parent": "uranus",
    "km": 1157.8,
    "basis": "twice the mean radius, 578.9 km",
    "source": {
      "label": "JPL satellite physical parameters",
      "url": "https://ssd.jpl.nasa.gov/sats/phys_par/"
    },
    "photo": {
      "file": "sizes/ariel.webp",
      "w": 256,
      "h": 256,
      "wKm": 1157.8,
      "hKm": 1157.8,
      "credit": "NASA/JPL",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA01534",
      "by": "Voyager 2",
      "date": "1986-01-24",
      "colour": "bw",
      "round": true,
      "v": "5a0cbfed"
    },
    "note": "Voyager 2, January 1986. Part of it was in darkness."
  },
  {
    "id": "dione",
    "name": "Dione",
    "kind": "moon",
    "parent": "saturn",
    "km": 1122.8,
    "basis": "twice the mean radius, 561.4 km",
    "source": {
      "label": "JPL satellite physical parameters",
      "url": "https://ssd.jpl.nasa.gov/sats/phys_par/"
    },
    "photo": {
      "file": "sizes/dione.webp",
      "w": 256,
      "h": 256,
      "wKm": 1122.8,
      "hKm": 1122.8,
      "credit": "NASA/JPL/Space Science Institute",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA07746",
      "by": "Cassini",
      "date": "2005-10-11",
      "colour": "bw",
      "round": true,
      "v": "49c365fa"
    },
    "note": "Cassini, October 2005."
  },
  {
    "id": "tethys",
    "name": "Tethys",
    "kind": "moon",
    "parent": "saturn",
    "km": 1062.2,
    "basis": "twice the mean radius, 531.1 km",
    "source": {
      "label": "JPL satellite physical parameters",
      "url": "https://ssd.jpl.nasa.gov/sats/phys_par/"
    },
    "photo": {
      "file": "sizes/tethys.webp",
      "w": 256,
      "h": 256,
      "wKm": 1062.2,
      "hKm": 1062.2,
      "credit": "NASA/JPL/Space Science Institute",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA07738",
      "by": "Cassini",
      "date": "2005-09",
      "colour": "bw",
      "round": true,
      "v": "259c8f04"
    },
    "note": "Cassini, September 2005."
  },
  {
    "id": "enceladus",
    "name": "Enceladus",
    "kind": "moon",
    "parent": "saturn",
    "km": 504.2,
    "basis": "twice the mean radius, 252.1 km",
    "source": {
      "label": "JPL satellite physical parameters",
      "url": "https://ssd.jpl.nasa.gov/sats/phys_par/"
    },
    "photo": {
      "file": "sizes/enceladus.webp",
      "w": 256,
      "h": 256,
      "wKm": 504.2,
      "hKm": 504.2,
      "credit": "NASA/JPL/Space Science Institute",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA11684",
      "by": "Cassini",
      "date": "2009-11-21",
      "colour": "bw",
      "round": true,
      "v": "7115eba8"
    },
    "note": "Cassini, November 2009."
  },
  {
    "id": "miranda",
    "name": "Miranda",
    "kind": "moon",
    "parent": "uranus",
    "km": 471.6,
    "basis": "twice the mean radius, 235.8 km",
    "source": {
      "label": "JPL satellite physical parameters",
      "url": "https://ssd.jpl.nasa.gov/sats/phys_par/"
    },
    "photo": {
      "file": "sizes/miranda.webp",
      "w": 256,
      "h": 256,
      "wKm": 471.6,
      "hKm": 471.6,
      "credit": "NASA/JPL-Caltech",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA18185",
      "by": "Voyager 2",
      "date": "1986-01-24",
      "colour": "bw",
      "round": true,
      "v": "86ffbb82"
    },
    "note": "Voyager 2, January 1986."
  },
  {
    "id": "mimas",
    "name": "Mimas",
    "kind": "moon",
    "parent": "saturn",
    "km": 396.4,
    "basis": "twice the mean radius, 198.2 km",
    "source": {
      "label": "JPL satellite physical parameters",
      "url": "https://ssd.jpl.nasa.gov/sats/phys_par/"
    },
    "photo": {
      "file": "sizes/mimas.webp",
      "w": 256,
      "h": 256,
      "wKm": 396.4,
      "hKm": 396.4,
      "credit": "NASA/JPL-Caltech/Space Science Institute",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA12570",
      "by": "Cassini",
      "date": "2010-02-13",
      "colour": "bw",
      "round": true,
      "v": "1cc67a3f"
    },
    "note": "Cassini, February 2010. The big crater is Herschel, 130 km wide."
  },
  {
    "id": "hyperion",
    "name": "Hyperion",
    "kind": "moon",
    "parent": "saturn",
    "km": 270.0,
    "basis": "twice the mean radius, 135 km; it is far from round",
    "source": {
      "label": "JPL satellite physical parameters",
      "url": "https://ssd.jpl.nasa.gov/sats/phys_par/"
    },
    "photo": {
      "file": "sizes/hyperion.webp",
      "w": 369,
      "h": 512,
      "wKm": 251.37,
      "hKm": 349.08,
      "credit": "NASA/JPL/Space Science Institute",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA07761",
      "by": "Cassini",
      "date": "2005-09-26",
      "colour": "bw",
      "v": "cbae5fe6"
    },
    "note": "Cassini, September 2005: six pictures joined. Each pixel covers 197 metres (as NASA gives it), which sets the scale; its longest side here is about 354 km."
  },
  {
    "id": "phobos",
    "name": "Phobos",
    "kind": "moon",
    "parent": "mars",
    "km": 22.16,
    "dims": [
      26.0,
      22.8,
      18.2
    ],
    "basis": "twice the mean radius, 11.08 km; it measures 26 by 22.8 by 18.2 km (NASA Mars fact sheet)",
    "source": {
      "label": "JPL satellite physical parameters",
      "url": "https://ssd.jpl.nasa.gov/sats/phys_par/"
    },
    "photo": {
      "file": "sizes/phobos.webp",
      "w": 768,
      "h": 733,
      "wKm": 20.0,
      "hKm": 19.09,
      "credit": "NASA/JPL-Caltech/University of Arizona",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA10368",
      "by": "Mars Reconnaissance Orbiter",
      "date": "2008-03-23",
      "colour": "enhanced",
      "v": "6390785c"
    },
    "note": "Mars Reconnaissance Orbiter, March 2008, with colours enhanced. Its sunlit part is about 21 km across (as NASA gives it), which sets the scale."
  },
  {
    "id": "deimos",
    "name": "Deimos",
    "kind": "moon",
    "parent": "mars",
    "km": 12.4,
    "dims": [
      15.6,
      12.0,
      10.2
    ],
    "basis": "twice the mean radius, 6.2 km; it measures 15.6 by 12 by 10.2 km (NASA Mars fact sheet)",
    "source": {
      "label": "JPL satellite physical parameters",
      "url": "https://ssd.jpl.nasa.gov/sats/phys_par/"
    },
    "photo": {
      "file": "sizes/deimos.webp",
      "w": 512,
      "h": 468,
      "wKm": 11.8,
      "hKm": 10.78,
      "credit": "NASA/JPL-Caltech/University of Arizona",
      "licence": "Public domain (NASA)",
      "page": "https://photojournal.jpl.nasa.gov/catalog/PIA11826",
      "by": "Mars Reconnaissance Orbiter",
      "date": "2009-02-21",
      "colour": "enhanced",
      "v": "d5092a08"
    },
    "note": "Mars Reconnaissance Orbiter, February 2009, with colours enhanced. The scale comes from the 2 km bar NASA published with it."
  },
  {
    "id": "arrokoth",
    "name": "Arrokoth",
    "kind": "kbo",
    "km": 18.3,
    "dims": [
      36.0,
      20.0,
      10.0
    ],
    "basis": "the diameter of a ball of the same volume; it measures 36 by 20 by 10 km",
    "source": {
      "label": "Spencer and others, 2020, Science",
      "url": "https://arxiv.org/abs/2004.00727"
    },
    "photo": {
      "file": "sizes/arrokoth.webp",
      "w": 1024,
      "h": 697,
      "wKm": 29.3,
      "hKm": 19.94,
      "credit": "NASA/Johns Hopkins Applied Physics Laboratory/Southwest Research Institute, National Optical Astronomy Observatory",
      "licence": "Used with permission (granted 6 Oct 2026)",
      "page": "https://science.nasa.gov/resource/highest-resolution-image-of-2014-mu69-ultima-thule/",
      "by": "New Horizons",
      "date": "2019-01-01",
      "colour": "bw",
      "v": "2669d2e7"
    },
    "note": "New Horizons, 1 January 2019, from 6,600 km: the farthest world ever visited, seen at a slant. The larger lobe, 20.6 km at its longest (Spencer and others, 2020), sets the scale."
  },
  {
    "id": "67p",
    "name": "Comet 67P",
    "kind": "comet",
    "km": 3.4,
    "dims": [
      4.3,
      2.6,
      2.1
    ],
    "basis": "the diameter of a ball of the same volume (Sierks and others, 2015); its best-fitting ellipsoid is 4.3 by 2.6 by 2.1 km (Spencer and others, 2020)",
    "source": {
      "label": "JPL Small-Body Database",
      "url": "https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=67P"
    },
    "photo": {
      "file": "sizes/67p.webp",
      "w": 945,
      "h": 674,
      "wKm": 5.0085,
      "hKm": 3.5722,
      "credit": "ESA/Rosetta/MPS for OSIRIS Team MPS/UPD/LAM/IAA/SSO/INTA/UPM/DASP/IDA",
      "licence": "ESA (with permission)",
      "page": "https://www.esa.int/ESA_Multimedia/Images/2014/08/Comet_on_3_August_2014",
      "by": "Rosetta",
      "date": "2014-08-03",
      "colour": "bw",
      "v": "efe45db0"
    },
    "note": "Rosetta, 3 August 2014, from 285 km; each pixel of the original covers 5.3 metres, which sets the scale."
  },
  {
    "id": "ryugu",
    "name": "Ryugu",
    "kind": "asteroid",
    "km": 0.896,
    "dims": [
      1.004,
      0.876
    ],
    "basis": "the diameter of a ball of the same volume (Watanabe and others, 2019); 1,004 m across its equator",
    "source": {
      "label": "JPL Small-Body Database",
      "url": "https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=162173"
    },
    "photo": {
      "file": "sizes/ryugu.webp",
      "w": 477,
      "h": 452,
      "wKm": 1.0194,
      "hKm": 0.966,
      "credit": "JAXA, University of Tokyo, Kochi University, Rikkyo University, Nagoya University, Chiba Institute of Technology, Meiji University, University of Aizu and AIST",
      "licence": "Used with permission (granted 6 Oct 2026)",
      "page": "https://www.hayabusa2.jaxa.jp/en/galleries/ryugu/pages/fig11_fmhome_front.html",
      "by": "Hayabusa2",
      "date": "2018-06-30",
      "colour": "bw",
      "v": "48a98369"
    },
    "note": "Hayabusa2, 30 June 2018, from about 20 km. Its width across the equator, 1,004 m (Watanabe and others, 2019), sets the scale."
  },
  {
    "id": "bennu",
    "name": "Bennu",
    "kind": "asteroid",
    "km": 0.48444,
    "basis": "twice its average radius, 242.22 m (Daly and others, 2020)",
    "source": {
      "label": "JPL Small-Body Database",
      "url": "https://ssd.jpl.nasa.gov/tools/sbdb_lookup.html#/?sstr=101955"
    },
    "photo": {
      "file": "sizes/bennu.webp",
      "w": 375,
      "h": 384,
      "wKm": 0.501,
      "hKm": 0.5126,
      "credit": "NASA/Goddard/University of Arizona",
      "licence": "Public domain (NASA)",
      "page": "https://science.nasa.gov/resource/bennu-mosaic/",
      "by": "OSIRIS-REx",
      "date": "2018-12-02",
      "colour": "bw",
      "v": "8fa7422f"
    },
    "note": "OSIRIS-REx, 2 December 2018, from 24 km. Its longest side in the picture is taken as 505 m, the longest axis of its best-fitting ellipsoid (Daly and others, 2020)."
  },
  {
    "id": "dimorphos",
    "name": "Dimorphos",
    "kind": "asteroid",
    "km": 0.151,
    "dims": [
      0.177,
      0.174,
      0.116
    ],
    "basis": "the diameter of a ball of the same volume; it measured 177 by 174 by 116 m before DART struck it",
    "source": {
      "label": "Daly and others, 2023, Nature",
      "url": "https://arxiv.org/abs/2303.02248"
    },
    "photo": {
      "file": "sizes/dimorphos.webp",
      "w": 359,
      "h": 384,
      "wKm": 0.1538,
      "hKm": 0.1647,
      "credit": "NASA/Johns Hopkins APL",
      "licence": "Used with permission (granted 6 Oct 2026)",
      "page": "https://www.nasa.gov/solar-system/darts-final-images-prior-to-impact/",
      "by": "DART",
      "date": "2022-09-26",
      "colour": "bw",
      "v": "e0e3415e"
    },
    "note": "DART, 11 seconds before it struck Dimorphos on 26 September 2022: the last picture to show all of it. Part of it was in shadow; its longest side, 177 m, sets the scale."
  }
];
